import { HelmetProvider } from 'react-helmet-async';
import { Helmet } from 'react-helmet-async';

const SEO_BASE = {
  siteName: 'A2B Towing & Roadside Assistance',
  siteUrl: 'https://a2btowing.org',
  phone: '+19162343923',
  phoneDisplay: '(916) 234-3923',
  email: 'A2broadside916@gmail.com',
  address: {
    street: 'Orangevale',
    city: 'Orangevale',
    state: 'CA',
    zip: '95662',
    country: 'US',
  },
  serviceAreas: ['Orangevale', 'Citrus Heights', 'El Dorado Hills', 'Folsom', 'Granite Bay', 'Rancho Cordova', 'Rocklin', 'Roseville'],
};

function SEOHead({ title, description, canonical, type = 'website', schema }) {
  const fullTitle = title ? `${title} | A2B Towing` : 'A2B Towing & Roadside Assistance - Orangevale CA | A2B Towing';
  const fullDescription = description || 'Professional towing & roadside assistance in Orangevale, CA. Fast response, flatbed towing, jump starts. Call (916) 234-3923';
  const canonicalUrl = canonical ? `${SEO_BASE.siteUrl}${canonical}` : SEO_BASE.siteUrl;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SEO_BASE.siteName} />
      <meta property="og:locale" content="en_US" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="geo.region" content="US-CA" />
      <meta name="geo.placename" content="Orangevale" />
      {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
    </Helmet>
  );
}

function getLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "TowingCompany",
    "name": "A2B Towing & Roadside Assistance",
    "alternateName": "A2B Towing",
    "url": "https://a2btowing.org",
    "telephone": "+1-916-234-3923",
    "email": "A2broadside916@gmail.com",
    "description": "Professional towing & roadside assistance in Orangevale, CA. Fast response, flatbed towing, jump starts, tire changes, fuel delivery, lockout service. Serving Sacramento County 24/7.",
    "image": "https://a2btowing.org/og-image.jpg",
    "logo": "https://a2btowing.org/og-image.jpg",
    "foundingDate": "2015",
    "sameAs": [
      "https://www.google.com/maps/place/A2B+Towing+%26+Roadside+Assistance"
    ],
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Orangevale",
      "addressRegion": "CA",
      "postalCode": "95662",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 38.6860,
      "longitude": -121.2261
    },
    "areaServed": SEO_BASE.serviceAreas.map(area => ({
      "@type": "City",
      "name": area,
      "containedInPlace": { "@type": "State", "name": "California" }
    })),
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    },
    "priceRange": "$$",
    "currenciesAccepted": "USD",
    "paymentAccepted": "Cash, Credit Card, Debit Card, Visa, Mastercard, American Express",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Towing & Roadside Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Towing Services",
            "description": "Fast and reliable flatbed towing for cars, trucks, SUVs, and motorcycles. Available 24/7."
          },
          "priceSpecification": {
            "@type": "PriceSpecification",
            "price": "150",
            "priceCurrency": "USD",
            "description": "Hook fee starting at $150, plus $4/mile enroute and $5/mile loaded"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Roadside Assistance",
            "description": "Jump starts, tire changes, fuel delivery, lockout service, battery replacement"
          },
          "priceSpecification": {
            "@type": "PriceSpecification",
            "price": "75",
            "priceCurrency": "USD",
            "description": "$75 flat rate for all roadside assistance services"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Hauling Services",
            "description": "Professional hauling for vehicles, equipment, and machinery"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Load Transfer",
            "description": "Safe and efficient load transfer between vehicles"
          }
        }
      ]
    },
    "potentialAction": [
      {
        "@type": "ReserveAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://a2btowing.org/quote",
          "actionPlatform": ["http://schema.org/DesktopWebPlatform", "http://schema.org/MobileWebPlatform"]
        },
        "result": {
          "@type": "Reservation",
          "name": "Towing Service Quote"
        }
      }
    ],
    "knowsAbout": [
      "Towing", "Flatbed Towing", "Roadside Assistance", "Jump Start", "Tire Change",
      "Fuel Delivery", "Lockout Service", "Vehicle Recovery", "Hauling", "Load Transfer",
      "Emergency Towing", "24/7 Towing", "Sacramento Towing", "Orangevale Towing"
    ]
  };
}

function getFAQSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
}

function getBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${SEO_BASE.siteUrl}${item.path}`
    }))
  };
}

function getServiceSchema(name, description) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": name,
    "description": description,
    "provider": {
      "@type": "TowingCompany",
      "name": "A2B Towing & Roadside Assistance",
      "telephone": "+1-916-234-3923"
    },
    "areaServed": SEO_BASE.serviceAreas.map(area => ({ "@type": "City", "name": area }))
  };
}

export { HelmetProvider, SEOHead, SEO_BASE, getLocalBusinessSchema, getFAQSchema, getBreadcrumbSchema, getServiceSchema };
